<template>
    <v-dialog
        :value="value"
        :max-width="maxWidth"
        scrollable
        :persistent="persistent"
        @input="$emit('input', $event)"
    >
        <v-card>
            <v-card-title class="d-flex align-center">
                <v-icon
                    v-if="icon"
                    :color="iconColor"
                    class="mr-2"
                >
                    {{ icon }}
                </v-icon>
                {{ resolvedTitle }}
            </v-card-title>
            <v-card-text>
                <p
                    v-if="showIntro"
                    class="mb-3"
                >
                    {{ resolvedIntro }}
                </p>
                <template v-if="currentGroup">
                    <div class="d-flex align-center justify-space-between mb-3">
                        <span class="subtitle-1 font-weight-medium">
                            {{ groupLabel(currentGroup) }}
                        </span>
                        <span
                            v-if="totalPages > 1"
                            class="text-caption grey--text text--darken-1"
                        >
                            {{ resolvedPageOfText }}
                        </span>
                    </div>
                    <ul class="mb-0 pl-4 paginated-messages-dialog__list">
                        <li
                            v-for="(message, index) in currentGroup.messages"
                            :key="'paginated-message-' + groupKey(currentGroup) + '-' + index"
                            class="mb-3 paginated-messages-dialog__item"
                        >
                            {{ message }}
                        </li>
                    </ul>
                </template>
            </v-card-text>
            <v-card-actions>
                <v-btn
                    v-if="totalPages > 1"
                    text
                    :disabled="pageIndex <= 0"
                    @click="prevPage"
                >
                    {{ resolvedPreviousLabel }}
                </v-btn>
                <v-spacer></v-spacer>
                <span
                    v-if="totalPages > 1 && requireAllPagesVisited && !allPagesVisited"
                    class="text-caption grey--text text--darken-1 mr-3"
                >
                    {{ resolvedReviewAllHint }}
                </span>
                <v-btn
                    v-if="pageIndex < totalPages - 1"
                    color="primary"
                    dark
                    @click="nextPage"
                >
                    {{ resolvedNextLabel }}
                </v-btn>
                <v-btn
                    v-if="canClose"
                    color="primary"
                    dark
                    @click="close"
                >
                    {{ resolvedCloseLabel }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'PaginatedMessagesDialog',
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        /** Grupos `{ key?, label?, messages[] }`. `label` tem prioridade sobre traduções. */
        groups: {
            type: Array,
            default: () => [],
        },
        /** Prefixo i18n para textos padrão do diálogo. */
        labelsPrefix: {
            type: String,
            default: 'paginatedMessagesDialog',
        },
        /** Prefixo i18n para rótulos de grupo (`${groupLabelsPrefix}.${key}`). */
        groupLabelsPrefix: {
            type: String,
            default: null,
        },
        /** Mapa explícito `key -> label`. */
        groupLabels: {
            type: Object,
            default: () => ({}),
        },
        title: {
            type: String,
            default: null,
        },
        intro: {
            type: String,
            default: null,
        },
        previousLabel: {
            type: String,
            default: null,
        },
        nextLabel: {
            type: String,
            default: null,
        },
        reviewAllHint: {
            type: String,
            default: null,
        },
        closeLabel: {
            type: String,
            default: null,
        },
        pageOfLabel: {
            type: String,
            default: null,
        },
        fallbackGroupLabel: {
            type: String,
            default: null,
        },
        requireAllPagesVisited: {
            type: Boolean,
            default: true,
        },
        showIntroWhenSingle: {
            type: Boolean,
            default: false,
        },
        maxWidth: {
            type: [Number, String],
            default: 640,
        },
        persistent: {
            type: Boolean,
            default: true,
        },
        icon: {
            type: String,
            default: 'mdi-alert',
        },
        iconColor: {
            type: String,
            default: 'warning',
        },
    },
    data() {
        return {
            pageIndex: 0,
            visitedPages: [],
        };
    },
    computed: {
        normalizedGroups() {
            return (this.groups || []).filter(
                (group) => group && Array.isArray(group.messages) && group.messages.length,
            );
        },
        totalPages() {
            return this.normalizedGroups.length;
        },
        totalCount() {
            return this.normalizedGroups.reduce(
                (total, group) => total + group.messages.length,
                0,
            );
        },
        currentGroup() {
            if (!this.normalizedGroups.length) return null;
            const index = Number(this.pageIndex) || 0;
            return this.normalizedGroups[index] || this.normalizedGroups[0];
        },
        allPagesVisited() {
            if (!this.requireAllPagesVisited || this.totalPages <= 1) return true;
            for (let i = 0; i < this.totalPages; i += 1) {
                if (!this.visitedPages[i]) return false;
            }
            return true;
        },
        canClose() {
            if (this.pageIndex < this.totalPages - 1) return false;
            return this.allPagesVisited;
        },
        showIntro() {
            if (!this.resolvedIntro) return false;
            if (this.showIntroWhenSingle) return this.totalCount > 0;
            return this.totalCount > 1;
        },
        resolvedTitle() {
            return this.title || this.$t(`${this.labelsPrefix}.title`);
        },
        resolvedIntro() {
            const template = this.intro || this.$t(`${this.labelsPrefix}.intro`);
            return this.interpolate(template, {
                count: this.totalCount,
                types: this.totalPages,
            });
        },
        resolvedPageOfText() {
            const template = this.pageOfLabel || this.$t(`${this.labelsPrefix}.pageOf`);
            return this.interpolate(template, {
                current: this.pageIndex + 1,
                total: this.totalPages,
            });
        },
        resolvedPreviousLabel() {
            return this.previousLabel || this.$t(`${this.labelsPrefix}.previous`);
        },
        resolvedNextLabel() {
            return this.nextLabel || this.$t(`${this.labelsPrefix}.next`);
        },
        resolvedReviewAllHint() {
            return this.reviewAllHint || this.$t(`${this.labelsPrefix}.reviewAllHint`);
        },
        resolvedCloseLabel() {
            return this.closeLabel || this.$t('button.close');
        },
        resolvedFallbackGroupLabel() {
            return this.fallbackGroupLabel || this.$t(`${this.labelsPrefix}.fallbackGroupLabel`);
        },
    },
    watch: {
        value(isOpen) {
            if (isOpen) this.resetPagination();
        },
        groups: {
            handler() {
                if (this.value) this.resetPagination();
            },
            deep: true,
        },
    },
    methods: {
        groupKey(group) {
            if (!group) return '';
            return group.key ?? group.type ?? '';
        },
        groupLabel(group) {
            if (!group) return '';
            if (group.label) return group.label;
            const key = this.groupKey(group);
            if (key && this.groupLabels[key]) return this.groupLabels[key];
            if (this.groupLabelsPrefix && key) {
                const translationKey = `${this.groupLabelsPrefix}.${key}`;
                const translated = this.$t(translationKey);
                if (translated !== translationKey) return translated;
            }
            return key || this.resolvedFallbackGroupLabel;
        },
        interpolate(template, values) {
            if (!template) return '';
            return String(template).replace(/\{(\w+)\}/g, (_, name) => {
                const value = values[name];
                return value == null ? '' : String(value);
            });
        },
        resetPagination() {
            this.pageIndex = 0;
            this.visitedPages = this.normalizedGroups.map((_, index) => index === 0);
        },
        markPageVisited(pageIndex) {
            const index = Number(pageIndex);
            if (!Number.isFinite(index) || index < 0) return;
            if (this.visitedPages[index]) return;
            this.$set(this.visitedPages, index, true);
        },
        prevPage() {
            if (this.pageIndex <= 0) return;
            this.pageIndex -= 1;
            this.markPageVisited(this.pageIndex);
        },
        nextPage() {
            if (this.pageIndex >= this.totalPages - 1) return;
            this.pageIndex += 1;
            this.markPageVisited(this.pageIndex);
        },
        close() {
            if (!this.canClose) return;
            this.$emit('input', false);
            this.$emit('close');
        },
    },
};
</script>

<style scoped>
.paginated-messages-dialog__item {
    white-space: pre-wrap;
}
</style>
